import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { MaskedInput } from "@/components/ui/masked-input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDocument } from "@/lib/validations";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Smartphone, AlertTriangle } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";

interface Driver {
  id: string;
  name: string;
  cpf?: string;
  cnh?: string;
  cnh_category?: string;
  cnh_valid_until?: string;
  phone?: string;
  email?: string;
  status: string;
}

interface DriverFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  mode?: 'create' | 'edit';
  initialData?: Driver | null;
}

export function DriverFormSheet({ 
  open, 
  onOpenChange, 
  companyId, 
  mode = 'create', 
  initialData = null 
}: DriverFormSheetProps) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [driverForm, setDriverForm] = useState({
    name: '',
    cpf: '',
    cnh: '',
    cnh_category: 'B',
    cnh_valid_until: '',
    phone: '',
    email: '',
    status: 'active'
  });

  const [enableMobileAccess, setEnableMobileAccess] = useState(false);
  const [mobilePin, setMobilePin] = useState('');
  const [confirmMobilePin, setConfirmMobilePin] = useState('');

  useEffect(() => {
    if (initialData && mode === 'edit') {
      setDriverForm({
        name: initialData.name,
        cpf: initialData.cpf || '',
        cnh: initialData.cnh || '',
        cnh_category: initialData.cnh_category || 'B',
        cnh_valid_until: initialData.cnh_valid_until || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        status: initialData.status
      });
    } else {
      resetForm();
    }
  }, [initialData, mode, open]);

  const createDriverMutation = useMutation({
    mutationFn: async (driverData: typeof driverForm) => {
      const cleanCpf = driverData.cpf?.replace(/\D/g, '') || '';
      
      const { data, error } = await supabase
        .from("fleet_drivers")
        .insert({
          ...driverData,
          cpf: cleanCpf,
          company_id: companyId,
          created_by: profile?.id,
          cnh_valid_until: driverData.cnh_valid_until || null
        })
        .select()
        .single();

      if (error) throw error;

      if (enableMobileAccess && mobilePin) {
        const { error: authError } = await supabase
          .from('fleet_driver_auth')
          .insert({
            driver_id: data.id,
            pin_code: mobilePin,
            is_active: true
          });

        if (authError) {
          console.error('Erro ao criar acesso mobile:', authError);
          throw new Error('Motorista criado, mas falha ao configurar acesso mobile');
        }
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["fleetDrivers"] });
      queryClient.invalidateQueries({ queryKey: ["driver-access"] });
      onOpenChange(false);
      toast({
        title: "Motorista criado!",
        description: enableMobileAccess 
          ? `${data.name} foi adicionado com acesso mobile habilitado.`
          : `${data.name} foi adicionado com sucesso.`,
      });
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro ao criar motorista",
        description: error.message,
      });
    },
  });

  const updateDriverMutation = useMutation({
    mutationFn: async (driverData: typeof driverForm) => {
      if (!initialData?.id) throw new Error("ID do motorista não encontrado");

      const cleanCpf = driverData.cpf?.replace(/\D/g, '') || '';

      const { data, error } = await supabase
        .from("fleet_drivers")
        .update({
          ...driverData,
          cpf: cleanCpf,
          cnh_valid_until: driverData.cnh_valid_until || null
        })
        .eq("id", initialData.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["fleetDrivers"] });
      onOpenChange(false);
      toast({
        title: "Motorista atualizado!",
        description: `${data.name} foi atualizado com sucesso.`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar motorista",
        description: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validação básica
    if (!driverForm.name.trim()) {
      toast({
        variant: "destructive",
        title: "Campo obrigatório",
        description: "O nome do motorista é obrigatório.",
      });
      return;
    }

    // Validação de PIN se acesso mobile habilitado
    if (mode === 'create' && enableMobileAccess) {
      if (!mobilePin || mobilePin.length !== 6) {
        toast({
          variant: "destructive",
          title: "PIN inválido",
          description: "O PIN deve ter exatamente 6 dígitos.",
        });
        return;
      }

      if (mobilePin !== confirmMobilePin) {
        toast({
          variant: "destructive",
          title: "PINs não coincidem",
          description: "Por favor, verifique se os PINs digitados são iguais.",
        });
        return;
      }
    }

    if (mode === 'create') {
      createDriverMutation.mutate(driverForm);
    } else {
      updateDriverMutation.mutate(driverForm);
    }
  };

  const resetForm = () => {
    setDriverForm({
      name: '',
      cpf: '',
      cnh: '',
      cnh_category: 'B',
      cnh_valid_until: '',
      phone: '',
      email: '',
      status: 'active'
    });
    setEnableMobileAccess(false);
    setMobilePin('');
    setConfirmMobilePin('');
  };

  const isLoading = createDriverMutation.isPending || updateDriverMutation.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[650px] p-6 overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'Novo Motorista' : 'Editar Motorista'}</SheetTitle>
          <SheetDescription>
            {mode === 'create' 
              ? 'Preencha os dados para cadastrar um novo motorista'
              : 'Atualize as informações do motorista'
            }
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <form id="driver-form" onSubmit={handleSubmit} className="space-y-6 py-4">
            {/* Dados Pessoais */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium border-b pb-2">Dados Pessoais</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={driverForm.name}
                    onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })}
                    placeholder="Nome completo do motorista"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cpf">CPF</Label>
                    <MaskedInput
                      id="cpf"
                      mask="document"
                      value={driverForm.cpf}
                      onChange={(cleanValue, maskedValue) => {
                        setDriverForm({ ...driverForm, cpf: maskedValue });
                      }}
                      placeholder="000.000.000-00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone</Label>
                    <MaskedInput
                      id="phone"
                      mask="phone"
                      value={driverForm.phone}
                      onChange={(cleanValue, maskedValue) => {
                        setDriverForm({ ...driverForm, phone: maskedValue });
                      }}
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={driverForm.email}
                    onChange={(e) => setDriverForm({ ...driverForm, email: e.target.value })}
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>
            </div>

            {/* Habilitação */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm">Habilitação (CNH)</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cnh">Número da CNH</Label>
                    <Input
                      id="cnh"
                      value={driverForm.cnh}
                      onChange={(e) => setDriverForm({ ...driverForm, cnh: e.target.value })}
                      placeholder="00000000000"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cnh_category">Categoria</Label>
                    <Select
                      value={driverForm.cnh_category}
                      onValueChange={(value) => setDriverForm({ ...driverForm, cnh_category: value })}
                    >
                      <SelectTrigger id="cnh_category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A - Moto</SelectItem>
                        <SelectItem value="B">B - Carro</SelectItem>
                        <SelectItem value="C">C - Caminhão leve</SelectItem>
                        <SelectItem value="D">D - Ônibus/Caminhão médio</SelectItem>
                        <SelectItem value="E">E - Caminhão pesado</SelectItem>
                        <SelectItem value="AB">AB - Moto e Carro</SelectItem>
                        <SelectItem value="AC">AC - Moto e Caminhão leve</SelectItem>
                        <SelectItem value="AD">AD - Moto e Ônibus</SelectItem>
                        <SelectItem value="AE">AE - Moto e Caminhão pesado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnh_valid_until">Validade da CNH</Label>
                  <DatePicker
                    value={driverForm.cnh_valid_until}
                    onChange={(date) => setDriverForm({ ...driverForm, cnh_valid_until: date || '' })}
                    placeholder="Selecione a data"
                  />
                </div>
              </div>
            </div>

            {/* Acesso Mobile */}
            {mode === 'create' && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="enableMobileAccess"
                    checked={enableMobileAccess}
                    onCheckedChange={(checked) => setEnableMobileAccess(checked as boolean)}
                  />
                  <Label htmlFor="enableMobileAccess" className="flex items-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    Habilitar acesso mobile
                  </Label>
                </div>

                {enableMobileAccess && (
                  <div className="space-y-4 pl-6 border-l-2 border-primary/20">
                    <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-md">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                      <p className="text-xs text-muted-foreground">
                        O motorista poderá acessar o aplicativo mobile usando o CPF cadastrado e o PIN definido abaixo.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="mobilePin">PIN (6 dígitos) *</Label>
                        <Input
                          id="mobilePin"
                          type="password"
                          inputMode="numeric"
                          maxLength={6}
                          value={mobilePin}
                          onChange={(e) => setMobilePin(e.target.value.replace(/\D/g, ''))}
                          placeholder="000000"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirmMobilePin">Confirmar PIN *</Label>
                        <Input
                          id="confirmMobilePin"
                          type="password"
                          inputMode="numeric"
                          maxLength={6}
                          value={confirmMobilePin}
                          onChange={(e) => setConfirmMobilePin(e.target.value.replace(/\D/g, ''))}
                          placeholder="000000"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Status */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm">Status</h3>
              <div className="space-y-2">
                <Select
                  value={driverForm.status}
                  onValueChange={(value) => setDriverForm({ ...driverForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </form>
        </ScrollArea>

        <SheetFooter className="border-t pt-4 mt-auto">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="driver-form"
            disabled={isLoading}
          >
            {isLoading ? "Salvando..." : mode === 'create' ? "Criar Motorista" : "Salvar Alterações"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
